import React, { useCallback, useEffect, useRef, useState } from 'react';
import ClanConnectLogo from '../../../assets/images/clan_connect_logo.png';
import './public-page-unavailable.scss';

/**
 * 404 for PUBLIC influencer pages (link-in-bio, storefront).
 *
 * Shown both when a slug never existed and when it did but the owner's plan no
 * longer includes the feature (trial ended, premium expired, downgraded to
 * Basic). The two cases are deliberately indistinguishable — a visitor has no
 * business learning that a page exists but its owner stopped paying, and the
 * API returns the same "not found" for both.
 *
 * Motion notes
 * ------------
 * Movement is driven by two CSS custom properties on the root — --cc-px and
 * --cc-py, each roughly -1..1 — which the stylesheet turns into card tilt and
 * layered parallax. They are written from a rAF loop that eases toward the
 * pointer, NOT from React state: a mousemove handler calling setState would
 * re-render the whole tree on every pointer sample. Nothing here re-renders
 * while the pointer moves.
 *
 * Everything is disabled under prefers-reduced-motion, including the JS loops.
 */

interface Props {
    /** What the visitor was looking for — tunes the copy only. */
    variant?: 'bio' | 'storefront' | 'generic';
    /** Overrides the default headline. */
    title?: string;
    /** Overrides the default sub-line. */
    message?: string;
}

const COPY: Record<string, { title: string; message: string }> = {
    bio: {
        title: 'This page is no longer available',
        message: 'The link you followed may be broken, or the page may have been removed.',
    },
    storefront: {
        title: 'This store is no longer available',
        message: 'The link you followed may be broken, or the store may have been removed.',
    },
    generic: {
        title: 'This page is no longer available',
        message: 'The link you followed may be broken, or the page may have been removed.',
    },
};

const BackIcon = () => (
    <svg className="cc-404__btn-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M9 14 4 9l5-5" />
        <path d="M4 9h10a6 6 0 0 1 0 12h-1" />
    </svg>
);

const prefersReducedMotion = (): boolean => {
    try {
        return !!window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch { return false; }
};

const isCoarsePointer = (): boolean => {
    try {
        return !!window.matchMedia && window.matchMedia('(pointer: coarse)').matches;
    } catch { return false; }
};

export const PublicPageUnavailable: React.FC<Props> = ({ variant = 'generic', title, message }) => {
    const copy = COPY[variant] || COPY.generic;
    const [nudge, setNudge] = useState(false);

    const rootRef = useRef<HTMLDivElement | null>(null);
    // Where the pointer wants the scene, and where it currently is. Easing the
    // second toward the first is what makes the parallax feel weighted instead
    // of snapping frame-to-frame with the cursor.
    const target = useRef({ x: 0, y: 0 });
    const current = useRef({ x: 0, y: 0 });
    const rafId = useRef<number | null>(null);

    // Single rAF loop: eases current -> target and writes the CSS variables.
    // Parks itself once the two converge so an idle page costs nothing.
    const runLoop = useCallback(() => {
        if (rafId.current !== null) return;
        const step = () => {
            const c = current.current;
            const t = target.current;
            c.x += (t.x - c.x) * 0.08;
            c.y += (t.y - c.y) * 0.08;

            const el = rootRef.current;
            if (el) {
                el.style.setProperty('--cc-px', c.x.toFixed(4));
                el.style.setProperty('--cc-py', c.y.toFixed(4));
            }

            const settled = Math.abs(t.x - c.x) < 0.001 && Math.abs(t.y - c.y) < 0.001;
            if (settled) {
                rafId.current = null;
                return;
            }
            rafId.current = requestAnimationFrame(step);
        };
        rafId.current = requestAnimationFrame(step);
    }, []);

    const handleMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
        const el = rootRef.current;
        if (!el || prefersReducedMotion()) return;
        const r = el.getBoundingClientRect();
        const x = (e.clientX - (r.left + r.width / 2)) / (r.width / 2);
        const y = (e.clientY - (r.top + r.height / 2)) / (r.height / 2);
        target.current = {
            x: Math.max(-1, Math.min(1, x)),
            y: Math.max(-1, Math.min(1, y)),
        };
        runLoop();
    }, [runLoop]);

    const handleLeave = useCallback(() => {
        target.current = { x: 0, y: 0 };
        runLoop();
    }, [runLoop]);

    // Touch devices never fire mousemove, so the scene would sit dead still.
    // Give it a slow autonomous drift instead, on the same CSS variables.
    useEffect(() => {
        if (!isCoarsePointer() || prefersReducedMotion()) return;
        let id = 0;
        const start = Date.now();
        const drift = () => {
            const t = (Date.now() - start) / 1000;
            const el = rootRef.current;
            if (el) {
                el.style.setProperty('--cc-px', (Math.sin(t * 0.55) * 0.6).toFixed(4));
                el.style.setProperty('--cc-py', (Math.cos(t * 0.4) * 0.45).toFixed(4));
            }
            id = requestAnimationFrame(drift);
        };
        id = requestAnimationFrame(drift);
        return () => cancelAnimationFrame(id);
    }, []);

    // Stop the pointer loop if the component unmounts mid-ease.
    useEffect(() => () => {
        if (rafId.current !== null) cancelAnimationFrame(rafId.current);
    }, []);

    const handleGoBack = () => {
        if (window.history.length > 1) {
            window.history.back();
        } else {
            window.location.href = 'https://www.clanconnect.ai';
        }
    };

    return (
        <div className="cc-404" ref={rootRef} onMouseMove={handleMove} onMouseLeave={handleLeave}>
            <div className="cc-404__card">
                <img className="cc-404__logo" src={ClanConnectLogo} alt="ClanConnect" />

                <div className="cc-404__digits" aria-hidden="true">
                    <span className="cc-404__digit cc-404__digit--left">4</span>
                    <button
                        type="button"
                        className={`cc-404__zero ${nudge ? 'is-nudged' : ''}`}
                        onClick={() => { setNudge(true); window.setTimeout(() => setNudge(false), 620); }}
                        aria-label="Bounce the zero"
                    >
                        {/* Orbiting accent dot — gives the focal glyph a heartbeat
                            without animating the ring itself (which would fight
                            the click bounce transform). */}
                        <span className="cc-404__orbit"><i className="cc-404__orbit-dot" /></span>
                    </button>
                    <span className="cc-404__digit cc-404__digit--right">4</span>
                </div>

                {/* The visible 404 above is decorative; this carries it to assistive tech. */}
                <p className="cc-404__sronly">Error 404 — page not found</p>

                <h1 className="cc-404__title">{title || copy.title}</h1>
                <p className="cc-404__message">{message || copy.message}</p>

                <div className="cc-404__actions">
                    <a className="cc-404__btn cc-404__btn--primary" href="https://www.clanconnect.ai">
                        <span className="cc-404__btn-label">Go to ClanConnect</span>
                        <span className="cc-404__btn-shine" aria-hidden="true" />
                    </a>
                    <button
                        type="button"
                        className="cc-404__btn cc-404__btn--ghost"
                        onClick={handleGoBack}
                    >
                        <BackIcon />
                        Go back
                    </button>
                </div>
            </div>

            <p className="cc-404__footnote">
                <span className="cc-404__footnote-line" aria-hidden="true" />
                Powered by ClanConnect
                <span className="cc-404__footnote-line" aria-hidden="true" />
            </p>
        </div>
    );
};

export default PublicPageUnavailable;
